/**
 * ReplyCopilotButton — drop-in landlord assist button that opens a
 * modal to draft a reply to a text/message/email. Pass the incoming
 * text + optional thread context; the copilot returns a polished
 * draft the landlord can copy or hand back via onDraft callback.
 *
 * Usage:
 *   <ReplyCopilotButton
 *     channel="email"                // 'email' | 'message' | 'sms'
 *     incoming={message.body}
 *     threadContext={optionalContext}
 *     onDraft={({subject, body}) => setReply(body)}
 *   />
 */
import { useState } from "react";
import { Modal, Button, Pill, Textarea } from "@/components/ui/Primitives";
import { Sparkles, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}` });

const TONES = [
  { value: "friendly", label: "Friendly"  },
  { value: "formal",   label: "Formal"    },
  { value: "firm",     label: "Firm"      },
];
const LANGS = [
  { value: "en", label: "EN" },
  { value: "fr", label: "FR" },
];

export default function ReplyCopilotButton({ channel = "message", incoming = "", threadContext = "", onDraft, className = "", label = "Draft reply", testid = "reply-copilot" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tone, setTone] = useState("friendly");
  const [lang, setLang] = useState("en");
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [copied, setCopied] = useState(false);

  async function generate() {
    if (!incoming.trim()) return toast.error("Nothing to reply to.");
    setBusy(true);
    setDraft({ subject: "", body: "" });
    try {
      const r = await fetch(`${API}/api/landlord-copilot/reply`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({
          channel,
          incoming_text: incoming,
          thread_context: threadContext || null,
          tone,
          want_language: lang,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Copilot failed");
      if (d.smart_engine_available === false || (!d.body && !d.subject)) {
        toast.info(d.notice || "Copilot is briefly unavailable — please try again in a moment.");
        return;
      }
      setDraft({ subject: d.subject || "", body: d.body || "" });
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }

  function use() {
    onDraft?.(draft);
    setOpen(false);
    toast.success("Draft loaded into your reply box.");
  }
  function copy() {
    const t = channel === "email" && draft.subject ? `Subject: ${draft.subject}\n\n${draft.body}` : draft.body;
    navigator.clipboard.writeText(t).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); if (!draft.body) generate(); }}
        className={`inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 hover:text-emerald-900 ${className}`}
        data-testid={`${testid}-btn`}
      >
        <Sparkles className="w-3.5 h-3.5"/> {label}
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Draft reply with the copilot" testid={`${testid}-modal`}>
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Pill tone="slate">{channel}</Pill>
            <div className="flex items-center gap-1">
              {TONES.map((t) => (
                <button key={t.value} onClick={() => setTone(t.value)} className={`text-[11px] px-2 py-1 rounded-full font-semibold ${tone === t.value ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`${testid}-tone-${t.value}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 ml-auto">
              {LANGS.map((l) => (
                <button key={l.value} onClick={() => setLang(l.value)} className={`text-[11px] px-2 py-1 rounded-full font-semibold ${lang === l.value ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`${testid}-lang-${l.value}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          {incoming && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 max-h-32 overflow-auto whitespace-pre-wrap">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Incoming</div>
              {incoming}
            </div>
          )}

          {busy ? (
            <div className="flex items-center justify-center py-10 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2"/> Drafting…
            </div>
          ) : draft.body ? (
            <div className="space-y-2">
              {channel === "email" && draft.subject && (
                <div className="text-xs">
                  <span className="text-slate-500 font-bold mr-1">Subject:</span>
                  <span className="text-slate-900 font-semibold" data-testid={`${testid}-subject`}>{draft.subject}</span>
                </div>
              )}
              <Textarea
                rows={7}
                value={draft.body}
                onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
                testid={`${testid}-body`}
              />
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <button onClick={copy} className="text-xs font-bold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid={`${testid}-copy`}>
                  {copied ? <><Check className="w-3 h-3"/> Copied</> : <><Copy className="w-3 h-3"/> Copy</>}
                </button>
                <Button onClick={generate} variant="secondary" className="!py-1.5 !px-3 text-xs" data-testid={`${testid}-regen`}>
                  <Sparkles className="w-3 h-3 inline mr-1"/> Re-draft
                </Button>
                {onDraft && (
                  <Button onClick={use} className="!py-1.5 !px-3 text-xs" data-testid={`${testid}-use`}>Use this draft</Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500 italic">No draft yet.</div>
          )}
        </div>
      </Modal>
    </>
  );
}
